import { useEffect, useRef } from "react";
import { TURNSTILE_SITE_KEY, turnstileEnabled } from "@/lib/turnstile";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      remove: (id: string) => void;
      reset: (id?: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;

const loadScript = () => {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Turnstile failed to load"));
    document.head.appendChild(s);
  });
  return scriptPromise;
};

interface Props {
  onVerify: (token: string) => void;
  /** Called when the check can't load or errors before ever issuing a token, so forms can stop gating on it. */
  onUnavailable?: () => void;
  /** "light" | "dark" | "auto" */
  theme?: "light" | "dark" | "auto";
  className?: string;
}

/**
 * Renders the Cloudflare Turnstile check. Invisible for real visitors in most
 * cases; shows a short challenge only when Cloudflare is unsure.
 */
const TurnstileWidget = ({ onVerify, onUnavailable, theme = "auto", className }: Props) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);
  const callback = useRef(onVerify);
  callback.current = onVerify;
  const unavailable = useRef(onUnavailable);
  unavailable.current = onUnavailable;

  useEffect(() => {
    if (!turnstileEnabled()) return;
    let cancelled = false;
    let issuedToken = false;

    loadScript()
      .then(() => {
        if (cancelled || !ref.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(ref.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme,
          callback: (token: string) => {
            issuedToken = true;
            callback.current(token);
          },
          "expired-callback": () => callback.current(""),
          "error-callback": () => {
            callback.current("");
            if (!issuedToken) unavailable.current?.();
          },
        });
      })
      .catch(() => {
        // Network blocked or Cloudflare unreachable: let the form submit as before.
        callback.current("");
        unavailable.current?.();
      });

    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          // widget already gone
        }
      }
    };
  }, [theme]);

  if (!turnstileEnabled()) return null;

  return <div ref={ref} className={className} />;
};

export default TurnstileWidget;
